import { AxiosResponse } from 'axios'
import { api } from 'boot/axios'
import { CustomLogin, CustomLoginParam } from 'src/system/models'
import { Profile } from '../profile'

import {
  AccessToken,
  RawAccessToken,
  RawSearchPhoneStatus,
  RawOTPToken,
  RawVerifyToken,
  SearchPhoneStatus,
  OTPToken,
  VerifyToken,
  RegisterParam,
  OptionalDatas,
  Branch,
} from './models'

export const getOptionalField = async () => {
  const response: AxiosResponse<OptionalDatas> = await api.get(
    'private/v1/optional-fields/'
  )
  return response.data
}

export const getBranch = async () => {
  const response: AxiosResponse<Branch[]> = await api.get('private/v1/branch/')
  return response.data
}

export const searchPhoneNumber = async (
  phoneCode: string,
  phoneNumber: string
): Promise<SearchPhoneStatus> => {
  const response: AxiosResponse<RawSearchPhoneStatus> = await api.post(
    'private/v1/auth/search/',
    {
      phone_code: phoneCode.replace('+', ''),
      phone_number: phoneNumber,
    }
  )

  return <SearchPhoneStatus>{
    available: response.data.available,
    needVerify: response.data.need_verify,
  }
}

export const loginWithPhoneAndPassword = async (
  phoneCode: string,
  phoneNumber: string,
  password: string,
  customLogin: CustomLogin | null = null
): Promise<AccessToken> => {
  const response: AxiosResponse<RawAccessToken> = await api.post(
    'private/v1/auth/login/',
    {
      phone_code: phoneCode.replace('+', ''),
      phone_number: phoneNumber,
      password: password,
      custom_params: customLogin,
    }
  )

  return <AccessToken>{
    accessToken: response.data.access_token,
    accessTokenExpiredDate: response.data.access_token_expired_date,
    refreshToken: response.data.refresh_token,
    refreshTokenExpiredDate: response.data.refresh_token_expired_date,
  }
}

export const sendOTP = async (
  phoneCode: string,
  phoneNumber: string
): Promise<OTPToken> => {
  const response: AxiosResponse<RawOTPToken> = await api.post(
    'private/v1/auth/otp/',
    {
      phone_code: phoneCode.replace('+', ''),
      phone_number: phoneNumber,
    }
  )

  return <OTPToken>{
    otpToken: response.data.otp_token,
  }
}

export const loginOTP = async (
  otpToken: string,
  otp: string,
  customLogin: CustomLogin | null = null
): Promise<AccessToken> => {
  const response: AxiosResponse<RawAccessToken> = await api.post(
    'private/v1/auth/login/otp/',
    {
      otp_token: otpToken,
      otp,
      custom_params: customLogin,
    }
  )

  return <AccessToken>{
    accessToken: response.data.access_token,
    accessTokenExpiredDate: response.data.access_token_expired_date,
    refreshToken: response.data.refresh_token,
    refreshTokenExpiredDate: response.data.refresh_token_expired_date,
  }
}

export const verifyOTP = async (
  otpToken: string,
  otp: string,
  fetchProfile = false
): Promise<VerifyToken> => {
  const response: AxiosResponse<RawVerifyToken> = await api.post(
    'private/v1/auth/otp/verify/',
    {
      otp_token: otpToken,
      otp,
      fetch_profile: fetchProfile,
    }
  )

  return <VerifyToken>{
    otpToken: response.data.otp_token,
    phoneCode: `+${response.data.phone_code}`,
    phoneNumber: response.data.phone_number,
    profile: response.data.profile
      ? <Profile>{
          id: response.data.profile.id,
          phoneCode: `+${response.data.profile.phone_code}`,
          phoneNumber: response.data.profile.phone_number,
          firstName: response.data.profile.first_name,
          lastName: response.data.profile.last_name,
          gender: response.data.profile.gender,
          birthdate: response.data.profile.birthdate,
          address: response.data.profile.address,
          city: response.data.profile.city,
          zip: response.data.profile.zip,
          branch: response.data.profile.branch,
          email: response.data.profile.email,
        }
      : null,
  }
}

export const register = async (
  profile: RegisterParam,
  customLogin: CustomLogin | null = null
): Promise<AccessToken> => {
  const response: AxiosResponse<RawAccessToken> = await api.post(
    'private/v1/auth/register/',
    {
      verify_token: profile.verifyToken,
      phone_code: profile.phoneCode.replace('+', ''),
      phone_number: profile.phoneNumber,
      password: profile.password,
      profile_first_name: profile.profileFirstName,
      profile_last_name: profile.profileLastName,
      profile_personal_id: profile.profilePersonalId,
      profile_email: profile.profileEmail,
      profile_birthdate: profile.profileBirthdate,
      profile_gender: profile.profileGender,
      profile_address: profile.profileAddress,
      profile_city: profile.profileCity,
      profile_zip: profile.profileZip,
      profile_branch: profile.profileBranch,
      custom_params: customLogin,
      optional_datas: profile.optionalDatas,
    }
  )

  const result: AccessToken = {
    accessToken: response.data.access_token,
    accessTokenExpiredDate: response.data.access_token_expired_date,
    refreshToken: response.data.refresh_token,
    refreshTokenExpiredDate: response.data.refresh_token_expired_date,
  }
  return result
}

export const loginWithCustomMethod = async (
  slug: string,
  params?: CustomLoginParam | null
): Promise<AccessToken> => {
  const response: AxiosResponse<RawAccessToken> = await api.post(
    'private/v1/auth/other/',
    {
      app_slug: slug,
      check_params: params,
    }
  )
  return <AccessToken>{
    accessToken: response.data.access_token,
    accessTokenExpiredDate: response.data.access_token_expired_date,
    refreshToken: response.data.refresh_token,
    refreshTokenExpiredDate: response.data.refresh_token_expired_date,
  }
}
